import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { apiService } from '@/utils/api';
import { FiWifi, FiBattery, FiCpu, FiThermometer, FiActivity, FiDroplet, FiPower } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Switcher from '@/components/ui/Switcher';
import { getOrpColor, getPhColor } from '@/utils/colorUtils';
import { useAnalytics } from './AnalyticsContext';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface StatusData {
  device_id: string;
  timestamp: string;
  is_chemistry_stable: boolean;
  is_running_calibration: boolean;
  is_backwash_on: boolean;
  is_spa_on: boolean;
  flow_check_status: number;
  dryrun_status: number;
  pump_on_reason: number;
  heater_mode: number;
  is_spa_heater_enable: boolean;
  is_solar_heater_enable: boolean;
  is_pool_heater_enable: boolean;
  wifi_strength: number;
  pump_speed: number;
  flow_rate: number;
  ph_value: number;
  orp_value: number;
}

interface DeviceOverview {
  status_data: StatusData;
  last_heartbeat: number;
}

const AnalyticsPage: React.FC = () => {
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<DeviceOverview | null>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!deviceId) return;
    
    const fetchDeviceOverview = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await apiService.get<DeviceOverview>(`/api/v1/sensor-data/overview?device_id=${deviceId}`);
        if (data) {
          setOverview(data);
        }
      } catch (err) {
        console.error('Error fetching device overview:', err);
        setError('Failed to fetch device overview');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDeviceOverview();
    const interval = setInterval(fetchDeviceOverview, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [deviceId]);

  const isDeviceOnline = (): boolean => {
    if (!overview) return false;
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime - overview.last_heartbeat < 300; // 5 minutes
  };

  const getSignalQuality = (signalStrength: number): { label: string; colorClass: string } => {
    if (signalStrength == 0) {
      return { label: 'Poor', colorClass: 'text-red-600 bg-red-100' };
    }
    if (signalStrength >= -50) {
      return { label: 'Excellent', colorClass: 'text-emerald-600 bg-emerald-100' };
    } else if (signalStrength >= -60) {
      return { label: 'Good', colorClass: 'text-blue-600 bg-blue-100' };
    } else if (signalStrength >= -70) {
      return { label: 'Fair', colorClass: 'text-amber-600 bg-amber-100' };
    } else {
      return { label: 'Poor', colorClass: 'text-red-600 bg-red-100' };
    }
  };

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>Device Overview</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>Monitor your device status and sensor data</p>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            {error}
          </div>
        ) : overview ? (
          <>

            {/* Sensor Data Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* pH Value Card */}
              <Card>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold">pH Value</h5>
                    <FiDroplet className="text-xl text-primary-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold">{overview.status_data.ph_value}</span>
                    <div className="mt-2">
                      <div 
                        className="h-2 w-full rounded-full"
                        style={{ backgroundColor: getPhColor(overview.status_data.ph_value) }}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* ORP Value Card */}
              <Card>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold">ORP Value</h5>
                    <FiActivity className="text-xl text-primary-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold">{overview.status_data.orp_value}</span>
                    <div className="mt-2">
                      <div 
                        className="h-2 w-full rounded-full"
                        style={{ backgroundColor: getOrpColor(overview.status_data.orp_value) }}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Flow Rate Card */}
              <Card>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold">Flow Rate</h5>
                    <FiActivity className="text-xl text-primary-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold">{overview.status_data.flow_rate} LPM</span>
                    <div className="mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        overview.status_data.flow_rate >= 30
                          ? 'text-emerald-600 bg-emerald-100'
                          : 'text-red-600 bg-red-100'
                      }`}>
                        {overview.status_data.flow_rate >= 30 ? 'Normal' : 'Low Flow'}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* WiFi Signal Card */}
              <Card>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold">WiFi Signal</h5>
                    <FiWifi className="text-xl text-primary-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold">{overview.status_data.wifi_strength} dBm</span>
                    <div className="mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSignalQuality(overview.status_data.wifi_strength).colorClass}`}>
                        {getSignalQuality(overview.status_data.wifi_strength).label}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Device Controls */}
            <Card>
              <div className="p-4">
                <h5 className="font-semibold mb-4">Device Controls</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Spa Mode</p>
                      <p className="text-sm text-gray-500">Enable spa operation</p>
                    </div>
                    <Switcher 
                      checked={overview.status_data.is_spa_on}
                      onChange={(val) => console.log('Spa Mode:', val)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Backwash</p>
                      <p className="text-sm text-gray-500">Start backwash cycle</p>
                    </div>
                    <Switcher 
                      checked={overview.status_data.is_backwash_on}
                      onChange={(val) => console.log('Backwash:', val)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Chemistry Stable</p>
                      <p className="text-sm text-gray-500">Water chemistry status</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      overview.status_data.is_chemistry_stable
                        ? 'text-emerald-600 bg-emerald-100'
                        : 'text-red-600 bg-red-100'
                    }`}>
                      {overview.status_data.is_chemistry_stable ? 'Stable' : 'Unstable'}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Spa Heater</p>
                      <p className="text-sm text-gray-500">Control spa heating</p>
                    </div>
                    <Switcher 
                      checked={overview.status_data.is_spa_heater_enable}
                      onChange={(val) => console.log('Spa Heater:', val)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Solar Heater</p>
                      <p className="text-sm text-gray-500">Control solar heating</p>
                    </div>
                    <Switcher 
                      checked={overview.status_data.is_solar_heater_enable}
                      onChange={(val) => console.log('Solar Heater:', val)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Pool Heater</p>
                      <p className="text-sm text-gray-500">Control pool heating</p>
                    </div>
                    <Switcher 
                      checked={overview.status_data.is_pool_heater_enable}
                      onChange={(val) => console.log('Pool Heater:', val)}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsPage; 